import { Button, Table } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { KeyedRow } from '../../components/KeyedRow';
import { PageHeader } from '../../components/PageHeader';
import { Title } from '../../components/Title';
import { useFilter } from '../../hooks/useFilter';
import { usePagination } from '../../hooks/usePagination';
import { queryWithFilter } from '../../util/api/queryWithFilter';
import { queryWithFilterAndPages } from '../../util/api/queryWithFilterAndPages';
import {
	formatToCurrency,
	formatToDateString,
	formatToTimeString,
} from '../../util/formatting';
import { undefinedCheck } from '../../util/undefinedCheck';
import { VatReturnExportModal } from './components/VatReturnExportModal';
import { VatStats } from './components/VatStats';
import type { VatDto } from './types/VatDto';

export function VatReports() {
	const { organisation } = useParams();
	const filter = useFilter();
	const pagination = usePagination();

	const [
		isExcelExportOpen,
		{ open: openExcelExport, close: closeExcelExport },
	] = useDisclosure(false);

	const vatQuery = queryWithFilterAndPages<VatDto[]>(
		`${organisation}/vat/all`,
		filter.value,
		pagination.value,
	);

	const vatCountQuery = queryWithFilter<number>(
		`${organisation}/vat/count`,
		filter.value,
	);

	return (
		<>
			<VatReturnExportModal
				opened={isExcelExportOpen}
				close={closeExcelExport}
			/>
			<PageHeader
				title={'VAT Reports'}
				filter={filter}
				rightContent={
					<Button
						color={'medepos.1'}
						variant={'filled'}
						leftSection={<IconFileSpreadsheet />}
						onClick={() => openExcelExport()}
					>
						Export VAT Return
					</Button>
				}
			/>
			<VatStats filter={filter.value} />
			<DataTable
				pagination={pagination}
				totalItems={vatCountQuery.data?.value}
				isRowsLoading={vatQuery.isLoading}
				isPaginationLoading={vatCountQuery.isLoading}
				rows={pagination.value.pageSize}
				minHeight={'54vh'}
				maxHeight={'67vh'}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th><Title label={'Identifier'} /></Table.Th>
						<Table.Th miw={145}><Title label={'Date'} /></Table.Th>
						<Table.Th><Title label={'Time'} /></Table.Th>
						<Table.Th><Title label={'VAT Rate'} /></Table.Th>
						<Table.Th><Title label={'VAT'} /></Table.Th>
						<Table.Th><Title label={'Gross Inc. Sales'} /></Table.Th>
						<Table.Th><Title label={'Gross No VAT'} /></Table.Th>
						<Table.Th><Title label={'Report No'} /></Table.Th>
						<Table.Th><Title label={'Branch'} /></Table.Th>
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{vatQuery.data?.value.map((element) => (
						<KeyedRow key={element.id}>
							<Table.Td>{element.id}</Table.Td>
							<Table.Td>{formatToDateString(element.dateTime)}</Table.Td>
							<Table.Td>{formatToTimeString(element.dateTime)}</Table.Td>
							<Table.Td>{element.rate}%</Table.Td>
							<Table.Td>{formatToCurrency(element.vat)}</Table.Td>
							<Table.Td>{formatToCurrency(element.gross)}</Table.Td>
							<Table.Td>
								{formatToCurrency(element.gross - element.vat)}
							</Table.Td>
							<Table.Td>
								{undefinedCheck(element.reportNo?.toString())}
							</Table.Td>
							<Table.Td>{undefinedCheck(element.branchName)}</Table.Td>
						</KeyedRow>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}
